import React from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    Chip,
    Collapse,
    FormControlLabel,
    IconButton,
    LinearProgress,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Tooltip from "@mui/material/Tooltip";
import {
    fmtEUR,
    getInvoicePaidAmount,
    getInvoiceOutstandingAmount,
    invoicePaymentStyles,
    invoiceTypeChipColor,
    invoiceTypeLabels,
    paymentLinkChipColor,
    paymentLinkStatusLabel,
    resolveInvoicePaymentStatus,
} from "./invoiceManagementUtils";

export default function InvoiceList({
    isOffer,
    isInvoiceLoading,
    invoiceTree,
    documentLabel,
    documentLabelPlural,
    payLinkFilter,
    setPayLinkFilter,
    showEmptyState,
    hasInvoices,
    groupedInvoices,
    sortedYears,
    expandedInvoiceGroups,
    toggleYearExpanded,
    toggleMonthExpanded,
    loadingInvoiceMonths,
    loadedInvoiceMonths,
    cancelledInvoiceIds,
    handleConvertOfferToInvoice,
    handleViewPdf,
    handlePaidChange,
    handleSentChange,
    handleOpenPartialPayment,
    handleOpenCancelDialog,
    handleOpenWorkOrderDialog,
    handleCreatePayLink,
    handleCopyPayLink,
    handleViewXRechnung,
    resolveInvoiceCustomerName,
}) {
    const renderInvoiceNode = (node, depth = 0) => {
        if (!node) return null;
        const children = Array.isArray(node.children) ? node.children : [];
        const isParent = children.length > 0;
        const typeLabel = invoiceTypeLabels[node.invoiceType] || node.invoiceType || documentLabel;
        const chipColor = invoiceTypeChipColor[node.invoiceType] || "default";

        const base = node.projectBase ?? node.totalGross ?? null;
        const consumed = node.projectConsumed ?? null;
        const remaining = node.projectRemaining ?? null;

        return (
            <Box
                key={node.id}
                sx={{
                    pl: depth * 2,
                    borderLeft: depth > 0 ? "2px solid rgba(0,0,0,0.06)" : "none",
                    ml: depth > 0 ? 1 : 0,
                    mt: depth > 0 ? 1 : 0,
                }}
            >
                <Box
                    sx={{
                        borderRadius: 2,
                        p: 1.5,
                        mb: 1,
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        border: "1px solid rgba(0,0,0,0.06)",
                    }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {node.invoiceNumber || documentLabel}
                                </Typography>
                                {node.invoiceType && (
                                    <Chip
                                        size="small"
                                        color={chipColor}
                                        variant={node.invoiceType === "STANDARD" ? "outlined" : "filled"}
                                        label={typeLabel}
                                    />
                                )}
                            </Stack>
                            {node.invoiceDate && (
                                <Typography variant="body2" color="text.secondary">
                                    {new Date(node.invoiceDate).toLocaleDateString("de-DE")}
                                </Typography>
                            )}
                            <Typography variant="body2">Betrag: {fmtEUR(node.totalGross)}</Typography>
                            {base != null && (
                                <Typography variant="body2" color="text.secondary">
                                    Projektbasis: {fmtEUR(base)}
                                </Typography>
                            )}
                            {remaining != null && (
                                <Typography
                                    variant="body2"
                                    color={remaining > 0 ? "warning.main" : "success.main"}
                                >
                                    Rest: {fmtEUR(remaining)}
                                </Typography>
                            )}
                        </Box>
                        <Stack spacing={0.5} alignItems={{ xs: "flex-start", sm: "flex-end" }}>
                            {typeof node.paid === "boolean" && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    color={node.paid ? "success" : "warning"}
                                    label={node.paid ? "Bezahlt" : "Offen"}
                                />
                            )}
                            {consumed != null && node.invoiceType !== "STANDARD" && (
                                <Typography variant="caption" color="text.secondary">
                                    Abgerechnet: {fmtEUR(Math.max(0, consumed))}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </Box>
                {isParent && children.map((child) => renderInvoiceNode(child, depth + 1))}
            </Box>
        );
    };

    return (
        <>
            {isInvoiceLoading && (
                <Box sx={{ mb: 2 }}>
                    <LinearProgress />
                </Box>
            )}

            {!isOffer && (
                <Box sx={{ mt: 3 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: "0 6px 24px rgba(0,0,0,0.06)" }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Projektübersicht
                            </Typography>
                            {invoiceTree.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    Noch keine Teil- oder Schlussrechnungen vorhanden.
                                </Typography>
                            ) : (
                                <Box>{invoiceTree.map((node) => renderInvoiceNode(node))}</Box>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            )}

            <Box sx={{ mt: 3 }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                >
                    <Typography variant="h6">Vorhandene {documentLabelPlural}</Typography>
                    {!isOffer && (
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            color="primary"
                            value={payLinkFilter}
                            onChange={(_, value) => {
                                if (value) setPayLinkFilter(value);
                            }}
                        >
                            <ToggleButton value="ALL">Alle</ToggleButton>
                            <ToggleButton value="SENT">Pay-Link gesendet</ToggleButton>
                            <ToggleButton value="PAID">Pay-Link bezahlt</ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </Stack>

                {showEmptyState && (
                    <Card sx={{ borderRadius: 3, boxShadow: "0 6px 24px rgba(0,0,0,0.06)" }}>
                        <CardContent>
                            <Typography variant="body1">
                                {hasInvoices
                                    ? `Keine ${documentLabelPlural} fuer den ausgewaehlten Filter.`
                                    : `Noch keine ${documentLabelPlural} vorhanden.`}
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                {sortedYears.map((year) => {
                    const monthsForYear = groupedInvoices[year] || {};
                    const sortedMonths = Object.keys(monthsForYear).sort((a, b) => {
                        if (a === "Unbekannt") return 1;
                        if (b === "Unbekannt") return -1;
                        return Number(b) - Number(a);
                    });
                    return (
                        <Box key={year} sx={{ mb: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <IconButton
                                    size="small"
                                    onClick={() => toggleYearExpanded(year)}
                                    aria-label={`Jahr ${year} ${
                                        expandedInvoiceGroups?.years?.[year] ? "einklappen" : "ausklappen"
                                    }`}
                                >
                                    {expandedInvoiceGroups?.years?.[year] ? (
                                        <ExpandLessRoundedIcon />
                                    ) : (
                                        <ExpandMoreRoundedIcon />
                                    )}
                                </IconButton>
                                <Typography variant="h6">{year}</Typography>
                            </Stack>
                            <Collapse in={!!expandedInvoiceGroups?.years?.[year]} timeout="auto" unmountOnExit>
                                {sortedMonths.map((monthKey) => {
                                    const monthLabel =
                                        monthKey === "Unbekannt"
                                            ? "Unbekannt"
                                            : new Date(Number(year), Number(monthKey), 1).toLocaleDateString("de-DE", {
                                                  month: "long",
                                              });
                                    const monthStateKey = `${year}-${monthKey}`;
                                    const monthInvoices = monthsForYear[monthKey] || [];
                                    const isMonthLoading = !isOffer && loadingInvoiceMonths.has(monthStateKey);
                                    const isMonthLoaded = isOffer || loadedInvoiceMonths.has(monthStateKey);
                                    return (
                                        <Box key={monthStateKey} sx={{ mb: 2 }}>
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleMonthExpanded(year, monthKey)}
                                                    aria-label={`${monthLabel} ${
                                                        expandedInvoiceGroups?.months?.[monthStateKey]
                                                            ? "einklappen"
                                                            : "ausklappen"
                                                    }`}
                                                >
                                                    {expandedInvoiceGroups?.months?.[monthStateKey] ? (
                                                        <ExpandLessRoundedIcon />
                                                    ) : (
                                                        <ExpandMoreRoundedIcon />
                                                    )}
                                                </IconButton>
                                                <Typography variant="subtitle1">{monthLabel}</Typography>
                                            </Stack>
                                            <Collapse
                                                in={!!expandedInvoiceGroups?.months?.[monthStateKey]}
                                                timeout="auto"
                                                unmountOnExit
                                            >
                                                {isMonthLoading && (
                                                    <Box sx={{ mb: 1 }}>
                                                        <LinearProgress />
                                                    </Box>
                                                )}
                                                {!isMonthLoading && isMonthLoaded && monthInvoices.length === 0 && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Keine {documentLabelPlural} in diesem Monat.
                                                    </Typography>
                                                )}
                                                {monthInvoices.length > 0 && (
                                                    <Grid2 container spacing={2}>
                                                        {monthInvoices.map((invoice) => {
                                                            const isCancelled = cancelledInvoiceIds.has(invoice?.id);
                                                            const isCancellationInvoice = invoice?.invoiceType === "CANCELLATION";
                                                            const isPaid = !!invoice.paid;
                                                            const paidAmount = getInvoicePaidAmount(invoice);
                                                            const outstanding = getInvoiceOutstandingAmount(invoice);
                                                            const showPaidAmount = !isPaid && paidAmount > 0;
                                                            const showOutstanding =
                                                                !isPaid && outstanding !== null && outstanding > 0;
                                                            const offerHasInvoice =
                                                                isOffer &&
                                                                Array.isArray(invoice.children) &&
                                                                invoice.children.some(
                                                                    (child) => child?.invoiceType !== "CANCELLATION"
                                                                );
                                                            const paymentStatus = resolveInvoicePaymentStatus(invoice);
                                                            const customerName = resolveInvoiceCustomerName(invoice);
                                                            const statusStyles =
                                                                invoicePaymentStyles[paymentStatus] ||
                                                                invoicePaymentStyles.open;
                                                            return (
                                                                <Grid2 key={invoice.id} xs={12} sm={6} md={4}>
                                                                    <Card
                                                                        sx={{
                                                                            borderRadius: 3,
                                                                            boxShadow: (theme) =>
                                                                                theme.palette.mode === "dark"
                                                                                    ? "0 6px 24px rgba(0,0,0,0.45)"
                                                                                    : "0 6px 24px rgba(0,0,0,0.06)",
                                                                            background: (theme) =>
                                                                                theme.palette.mode === "dark"
                                                                                    ? theme.palette.background.paper
                                                                                    : statusStyles.background,
                                                                            border: (theme) =>
                                                                                theme.palette.mode === "dark"
                                                                                    ? `1px solid ${theme.palette.divider}`
                                                                                    : statusStyles.border,
                                                                        }}
                                                                    >
                                                                        <CardContent>
                                                                            <Stack spacing={0.75}>
                                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                                    <Typography
                                                                                        variant="body1"
                                                                                        noWrap
                                                                                        title={`${invoice.invoiceNumber} - ${customerName}`}
                                                                                    >
                                                                                        {invoice.invoiceNumber}
                                                                                        <Typography
                                                                                            variant="caption"
                                                                                            component="span"
                                                                                            sx={{ ml: 1, color: "text.secondary" }}
                                                                                        >
                                                                                            {customerName}
                                                                                        </Typography>
                                                                                    </Typography>
                                                                                    {invoice.invoiceType && (
                                                                                        <Chip
                                                                                            size="small"
                                                                                            color={
                                                                                                invoiceTypeChipColor[invoice.invoiceType] ||
                                                                                                "default"
                                                                                            }
                                                                                            variant={
                                                                                                invoice.invoiceType === "STANDARD"
                                                                                                    ? "outlined"
                                                                                                    : "filled"
                                                                                            }
                                                                                            label={
                                                                                                invoiceTypeLabels[invoice.invoiceType] ||
                                                                                                invoice.invoiceType
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                    {!isOffer && invoice.deliveryRequested && !invoice.sent && (
                                                                                        <Chip
                                                                                            size="small"
                                                                                            color="warning"
                                                                                            variant="outlined"
                                                                                            label="Versand laeuft"
                                                                                        />
                                                                                    )}
                                                                                    {!isOffer &&
                                                                                        !invoice.sent &&
                                                                                        invoice.deliveryFailureCode && (
                                                                                            <Chip
                                                                                                size="small"
                                                                                                color="error"
                                                                                                variant="outlined"
                                                                                                label="Versand fehlgeschlagen"
                                                                                            />
                                                                                        )}
                                                                                </Stack>
                                                                                {(invoice.customer?.name || invoice.customer?.lastname) && (
                                                                                    <Typography variant="body2" color="text.secondary">
                                                                                        {invoice.customer?.name} {invoice.customer?.lastname}
                                                                                    </Typography>
                                                                                )}
                                                                                {invoice.invoiceDate && (
                                                                                    <Typography variant="body2" color="text.secondary">
                                                                                        Datum: {new Date(
                                                                                            invoice.invoiceDate
                                                                                        ).toLocaleDateString("de-DE")}
                                                                                    </Typography>
                                                                                )}
                                                                                {typeof invoice.totalGross === "number" && (
                                                                                    <Typography variant="body2">
                                                                                        Betrag: {fmtEUR(invoice.totalGross)}
                                                                                    </Typography>
                                                                                )}
                                                                                {!isOffer && showPaidAmount && (
                                                                                    <Typography variant="body2" color="text.secondary">
                                                                                        Bereits bezahlt: {fmtEUR(paidAmount)}
                                                                                    </Typography>
                                                                                )}
                                                                                {!isOffer && showOutstanding && (
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        color={
                                                                                            outstanding > 0 ? "warning.main" : "success.main"
                                                                                        }
                                                                                    >
                                                                                        noch offen: {fmtEUR(outstanding)}
                                                                                    </Typography>
                                                                                )}
                                                                                {!isOffer &&
                                                                                    invoice.paymentLinkStatus &&
                                                                                    invoice.paymentLinkStatus !== "NONE" && (
                                                                                        <Chip
                                                                                            size="small"
                                                                                            variant="outlined"
                                                                                            color={
                                                                                                paymentLinkChipColor[
                                                                                                    invoice.paymentLinkStatus
                                                                                                ] || "default"
                                                                                            }
                                                                                            label={
                                                                                                paymentLinkStatusLabel[
                                                                                                    invoice.paymentLinkStatus
                                                                                                ] || invoice.paymentLinkStatus
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                            </Stack>
                                                                        </CardContent>
                                                                        <CardActions
                                                                            sx={{
                                                                                justifyContent: "space-between",
                                                                                alignItems: "center",
                                                                                flexWrap: "wrap",
                                                                                gap: 1,
                                                                            }}
                                                                        >
                                                                            {isOffer ? (
                                                                                <Stack
                                                                                    direction={{ xs: "column", sm: "row" }}
                                                                                    spacing={1}
                                                                                    alignItems="center"
                                                                                    flexWrap="wrap"
                                                                                >
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        onClick={() => handleConvertOfferToInvoice(invoice)}
                                                                                        disabled={offerHasInvoice}
                                                                                    >
                                                                                        {offerHasInvoice
                                                                                            ? "Rechnung bereits erstellt"
                                                                                            : "Rechnung erstellen"}
                                                                                    </Button>
                                                                                    <Button size="small" onClick={() => handleViewPdf(invoice)}>
                                                                                        PDF ansehen
                                                                                    </Button>
                                                                                </Stack>
                                                                            ) : (
                                                                                <>
                                                                                    <Stack
                                                                                        direction="row"
                                                                                        spacing={1}
                                                                                        alignItems="center"
                                                                                        flexWrap="wrap"
                                                                                    >
                                                                                        <FormControlLabel
                                                                                            control={
                                                                                                <Checkbox
                                                                                                    checked={!!invoice.paid}
                                                                                                    onChange={(e) =>
                                                                                                        handlePaidChange(
                                                                                                            invoice,
                                                                                                            e.target.checked
                                                                                                        )
                                                                                                    }
                                                                                                    disabled={isCancelled}
                                                                                                />
                                                                                            }
                                                                                            label="Bezahlt"
                                                                                        />
                                                                                        <FormControlLabel
                                                                                            control={
                                                                                                <Checkbox
                                                                                                    checked={!!invoice.sent}
                                                                                                    onChange={(e) =>
                                                                                                        handleSentChange(
                                                                                                            invoice,
                                                                                                            e.target.checked
                                                                                                        )
                                                                                                    }
                                                                                                    disabled={isCancelled && !isCancellationInvoice}
                                                                                                />
                                                                                            }
                                                                                            label="Versendet"
                                                                                        />
                                                                                    </Stack>
                                                                                    <Stack
                                                                                        direction={{ xs: "column", sm: "row" }}
                                                                                        spacing={1}
                                                                                        alignItems="center"
                                                                                        flexWrap="wrap"
                                                                                    >
                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="outlined"
                                                                                            onClick={() => handleOpenPartialPayment(invoice)}
                                                                                            disabled={!!invoice.paid || isCancelled}
                                                                                        >
                                                                                            Teilzahlung erfassen
                                                                                        </Button>
                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="outlined"
                                                                                            color="error"
                                                                                            onClick={() => handleOpenCancelDialog(invoice)}
                                                                                            disabled={isCancelled}
                                                                                        >
                                                                                            Stornieren
                                                                                        </Button>
                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="outlined"
                                                                                            onClick={() => handleOpenWorkOrderDialog(invoice)}
                                                                                            disabled={isCancelled}
                                                                                        >
                                                                                            Auftrag zuordnen
                                                                                        </Button>
                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="outlined"
                                                                                            onClick={() => handleCreatePayLink(invoice)}
                                                                                            disabled={
                                                                                                invoice.paymentLinkStatus === "PAID" ||
                                                                                                isCancelled
                                                                                            }
                                                                                        >
                                                                                            {invoice.paymentLinkUrl
                                                                                                ? "Pay-Link erneut senden"
                                                                                                : "Pay-Link senden"}
                                                                                        </Button>
                                                                                        {invoice.paymentLinkUrl && (
                                                                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                                                                <Button
                                                                                                    size="small"
                                                                                                    onClick={() => {
                                                                                                        if (
                                                                                                            invoice.paymentLinkUrl &&
                                                                                                            typeof window !== "undefined"
                                                                                                        ) {
                                                                                                            window.open(
                                                                                                                invoice.paymentLinkUrl,
                                                                                                                "_blank",
                                                                                                                "noopener"
                                                                                                            );
                                                                                                        }
                                                                                                    }}
                                                                                                >
                                                                                                    Pay-Link oeffnen
                                                                                                </Button>
                                                                                                <Tooltip title="Pay-Link kopieren">
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        onClick={() => handleCopyPayLink(invoice)}
                                                                                                        aria-label="Pay-Link kopieren"
                                                                                                    >
                                                                                                        <ContentCopyIcon fontSize="inherit" />
                                                                                                    </IconButton>
                                                                                                </Tooltip>
                                                                                            </Stack>
                                                                                        )}
                                                                                        {!isOffer && (
                                                                                            <Button
                                                                                                size="small"
                                                                                                onClick={() => handleViewXRechnung(invoice)}
                                                                                            >
                                                                                                XRechnung
                                                                                            </Button>
                                                                                        )}
                                                                                        <Button size="small" onClick={() => handleViewPdf(invoice)}>
                                                                                            PDF ansehen
                                                                                        </Button>
                                                                                    </Stack>
                                                                                </>
                                                                            )}
                                                                        </CardActions>
                                                                    </Card>
                                                                </Grid2>
                                                            );
                                                        })}
                                                    </Grid2>
                                                )}
                                            </Collapse>
                                        </Box>
                                    );
                                })}
                            </Collapse>
                        </Box>
                    );
                })}
            </Box>
        </>
    );
}
